import { ReactElement } from 'react';
import {
  Button,
  FileUploadField,
  FormFieldState,
  RequestTypeBadge,
  StepIndicator,
  TextAreaField,
  UploadedFile,
} from '../../../../core/components';
import { useIsDesktop, usePageTitle } from '../../../../core/hooks';
import { tokens } from '../../../../core/theme';
import { REQUEST_TYPE_CONFIG, RequestType } from '../../../../core/types';
import {
  BUTTON_GROUP_GAP,
  DESKTOP_HEADER_GAP,
  FORM_MAX_WIDTH,
  MOBILE_HEADER_GAP,
  TOTAL_STEPS,
} from '../../constants';
import { DescriptionFieldName, useAutoFocusRef, usePreloadRecaptcha } from '../../hooks';
import { Channel } from '../../types/channel';
import {
  ACCEPTED_FILE_EXTENSIONS,
  DESCRIPTION_MAX_LENGTH,
  DescriptionFormValues,
  MAX_FILES,
} from '../../types/descriptionForm';
import { SelectionSummaryCard } from '../SelectionSummaryCard/SelectionSummaryCard';

const DESCRIPTION_PLACEHOLDER =
  'Se possível inserir dados como: número da nota fiscal, nome do produto, data da compra e entre outros...';
const UPLOAD_INSTRUCTIONS =
  'Envie até 2 arquivos nos formatos JPG, JPEG, PNG ou PDF, com limite de 10 MB por arquivo.';

const GOOGLE_PRIVACY_URL = 'https://policies.google.com/privacy';
const GOOGLE_TERMS_URL = 'https://policies.google.com/terms';

// Fixo nos dois breakpoints no Figma.
const FORM_GAP = 12;
const MOBILE_UPLOAD_GROUP_GAP = 12;
const DESKTOP_UPLOAD_GROUP_GAP = 24;

type DescriptionStepProps = {
  type: RequestType;
  channel: Channel;
  themeLabel: string | undefined;
  values: DescriptionFormValues;
  files: UploadedFile[];
  isValid: boolean;
  handleDescriptionChange: (value: string) => void;
  handleBlur: (field: DescriptionFieldName) => void;
  getFieldState: (field: DescriptionFieldName) => FormFieldState;
  getFieldError: (field: DescriptionFieldName) => string | undefined;
  handleAddFiles: (fileList: FileList) => void;
  handleRemoveFile: (id: string) => void;
  fileUploadError?: string;
  isSubmitting?: boolean;
  isStoreTypeLoading?: boolean;
  onBack: () => void;
  onContinue: () => void;
};

export const DescriptionStep = ({
  type,
  channel,
  themeLabel,
  values,
  files,
  isValid,
  handleDescriptionChange,
  handleBlur,
  getFieldState,
  getFieldError,
  handleAddFiles,
  handleRemoveFile,
  fileUploadError,
  isSubmitting = false,
  isStoreTypeLoading = false,
  onBack,
  onContinue,
}: DescriptionStepProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const headingRef = useAutoFocusRef<HTMLHeadingElement>();
  const { icon: requestTypeIcon, label: requestTypeLabel } = REQUEST_TYPE_CONFIG[type];
  usePageTitle(`${requestTypeLabel} — Descrição`);
  usePreloadRecaptcha();

  return (
    <div style={styles.page}>
      <div style={{ ...styles.form, gap: FORM_GAP }}>
        <div style={{ ...styles.header, gap: isDesktop ? DESKTOP_HEADER_GAP : MOBILE_HEADER_GAP }}>
          <div style={styles.badgeRow}>
            <RequestTypeBadge icon={requestTypeIcon} label={requestTypeLabel} />
            <StepIndicator currentStep={3} totalSteps={TOTAL_STEPS} />
          </div>
          <SelectionSummaryCard channel={channel} themeLabel={themeLabel} />
        </div>
        <div style={styles.descriptionSection}>
          <h1 ref={headingRef} tabIndex={-1} style={styles.sectionTitle}>
            Descrição
          </h1>
          <TextAreaField
            id="description"
            label="Descrição"
            value={values.description}
            placeholder={DESCRIPTION_PLACEHOLDER}
            maxLength={DESCRIPTION_MAX_LENGTH}
            state={getFieldState('description')}
            error={getFieldError('description')}
            onChange={handleDescriptionChange}
            onBlur={() => handleBlur('description')}
          />
        </div>
        <div style={styles.divider} />
        <div
          style={{
            ...styles.uploadGroup,
            gap: isDesktop ? DESKTOP_UPLOAD_GROUP_GAP : MOBILE_UPLOAD_GROUP_GAP,
          }}
        >
          <FileUploadField
            id="files"
            label="Carregar arquivos"
            instructions={UPLOAD_INSTRUCTIONS}
            files={files}
            maxFiles={MAX_FILES}
            accept={ACCEPTED_FILE_EXTENSIONS}
            error={fileUploadError}
            onAddFiles={handleAddFiles}
            onRemoveFile={handleRemoveFile}
          />
          {/* O selo flutuante do reCAPTCHA fica oculto (index.css); o Google exige este aviso no lugar. */}
          <p style={styles.captchaNotice}>
            Este site é protegido pelo reCAPTCHA, e se aplicam a{' '}
            <a href={GOOGLE_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              Política de Privacidade
            </a>{' '}
            e os{' '}
            <a href={GOOGLE_TERMS_URL} target="_blank" rel="noopener noreferrer">
              Termos de Serviço
            </a>{' '}
            do Google.
          </p>
          <div style={styles.buttonGroup}>
            <Button variant="secondary" onClick={onBack}>
              Voltar
            </Button>
            <Button
              variant="primary"
              disabled={!isValid || isSubmitting || isStoreTypeLoading}
              onClick={onContinue}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: `${tokens.spacing.default} ${tokens.spacing.pageGutter}`,
    paddingBottom: tokens.spacing.large,
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: FORM_MAX_WIDTH,
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  descriptionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.medium,
    width: '100%',
  },
  sectionTitle: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.sectionTitle,
    fontWeight: tokens.typography.fontWeight.semiBold,
    color: tokens.colors.brand.gray.DEFAULT,
    textTransform: 'uppercase',
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: tokens.colors.brand.gray.light850,
  },
  uploadGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  captchaNotice: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.regular,
    color: tokens.colors.brand.gray.DEFAULT,
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: BUTTON_GROUP_GAP,
    width: '100%',
  },
};
